import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import InstagramIcon from '@mui/icons-material/Instagram';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import { Box, Card, Container, IconButton, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import { useState } from 'react';
import blogHeaderImage from '../../assets/landing/img/blog-header-medium.png';
import ggLogoBlue from '../../assets/landing/img/gg-logo-blue-vertical.svg';
import teamGGDemoImage from '../../assets/landing/img/team-gg-demo.jpg';
import { SOCIAL_LINKS } from '../../common/constants';
import { landingTheme } from './theme';

interface BlogPost {
  title: string;
  subtitle: string;
  image: string;
  link: string;
  linkText: string;
  imageSize?: 'cover' | 'contain';
}

const blogPosts: BlogPost[] = [
  {
    title: 'Social Responsibility',
    subtitle:
      "Learn how we integrated social responsibility into Grant Guardian's development through a principles-first approach.",
    image: blogHeaderImage,
    link: 'https://medium.com/patrick-j-mcgovern-foundation/social-responsibility-comes-first-product-development-lessons-from-grant-guardian-3c5fe6916019',
    linkText: 'Read the blog',
    imageSize: 'cover',
  },
  {
    title: 'Data Security & Privacy',
    subtitle:
      'Learn how we protect your sensitive financial data with enterprise-grade security.',
    image: ggLogoBlue,
    link: 'https://www.mcgovern.org/our-work/data-solutions/grant-guardian/',
    linkText: 'View details',
    imageSize: 'contain',
  },
  {
    title: 'Financial Due Diligence in Practice',
    subtitle:
      'Learn how foundations are using Grant Guardian to reduce manual due diligence.',
    image: teamGGDemoImage,
    link: 'https://www.youtube.com/playlist?list=PLvf0cULRA3jrDSV_bXWRyggC0RYzEgzin',
    linkText: 'Explore more',
    imageSize: 'cover',
  },
];

export default function Content() {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? blogPosts.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === blogPosts.length - 1 ? 0 : prev + 1));
  };

  const currentPost = blogPosts[currentIndex];

  return (
    <Box
      id="content"
      sx={{
        py: 10,
        backgroundColor: 'grey.50',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        {/* Carousel */}
        <Box sx={{ position: 'relative', maxWidth: '1400px', mx: 'auto' }}>
          {/* Previous Button */}
          <IconButton
            onClick={handlePrevious}
            sx={{
              position: 'absolute',
              left: { xs: -20, md: -60 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'white',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: landingTheme.colors.primary,
                color: 'white',
              },
            }}
          >
            <ArrowBackIosNewIcon />
          </IconButton>

          {/* Blog Card */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
          >
            <Card
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                overflow: 'hidden',
                borderRadius: '16px',
                boxShadow: 3,
              }}
            >
              {/* Left Content */}
              <Box
                sx={{
                  flex: 1,
                  p: { xs: 4, md: 6 },
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  backgroundColor: 'white',
                }}
              >
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      fontSize: { xs: '1.5rem', md: '2rem' },
                    }}
                  >
                    {currentPost.title}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 400,
                      mb: 3,
                      color: 'text.secondary',
                      fontSize: { xs: '1rem', md: '1.25rem' },
                    }}
                  >
                    {currentPost.subtitle}
                  </Typography>
                  <Box
                    component="a"
                    href={currentPost.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: landingTheme.colors.primary,
                      textDecoration: 'underline',
                      fontWeight: 600,
                      '&:hover': {
                        color: landingTheme.colors.darkTeal,
                      },
                    }}
                  >
                    {currentPost.linkText}
                  </Box>
                </Box>

                {/* Social Links */}
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 4 }}
                >
                  <IconButton
                    size="small"
                    href={SOCIAL_LINKS.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <LinkedInIcon
                      sx={{ fontSize: 16, color: 'text.secondary' }}
                    />
                  </IconButton>
                  <IconButton
                    size="small"
                    href={SOCIAL_LINKS.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <InstagramIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    href={SOCIAL_LINKS.x}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <XIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                  </IconButton>
                  <Box
                    component="a"
                    href={SOCIAL_LINKS.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      textDecoration: 'none',
                      '&:hover': {
                        opacity: 0.7,
                      },
                    }}
                  >
                    <LanguageIcon
                      sx={{ fontSize: 16, color: 'text.secondary' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      www.mcgovern.org
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Right Image */}
              <Box
                sx={{
                  flex: { xs: 1, md: 3 },
                  minHeight: { xs: 250, md: 400 },
                  backgroundImage: `url(${currentPost.image})`,
                  backgroundSize: currentPost.imageSize || 'contain',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    borderRadius: '50%',
                    width: 48,
                    height: 48,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    color: landingTheme.colors.primary,
                  }}
                >
                  0{currentIndex + 1}
                </Box>
              </Box>
            </Card>
          </motion.div>

          {/* Next Button */}
          <IconButton
            onClick={handleNext}
            sx={{
              position: 'absolute',
              right: { xs: -20, md: -60 },
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              backgroundColor: 'white',
              boxShadow: 2,
              '&:hover': {
                backgroundColor: landingTheme.colors.primary,
                color: 'white',
              },
            }}
          >
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>

        {/* Dots Indicator */}
        {blogPosts.length > 1 && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 1,
              mt: 4,
            }}
          >
            {blogPosts.map((_, index) => (
              <Box
                key={index}
                onClick={() => setCurrentIndex(index)}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  backgroundColor:
                    index === currentIndex
                      ? landingTheme.colors.primary
                      : 'grey.300',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: landingTheme.colors.primary,
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Container>
    </Box>
  );
}
